// pages/Help.tsx - UPDATED Google Adsense Style
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  QuestionMarkCircleIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CogIcon,
  UserGroupIcon,
  ArrowPathIcon,
  XMarkIcon,
  BoltIcon,
  LightBulbIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
}

interface Guide {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  url: string;
  category: string;
}

const Help: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    subject: "general",
    message: "",
  });

  const faqs: FAQ[] = [
    {
      id: "1",
      question: "How do I generate historical data?",
      answer:
        "Go to Admin > Data Generation. Select a user, date range, and choose between Simple Mode (enter total amounts) or Complex Mode (detailed parameters). Click 'Generate Data' to create sample data.",
      category: "admin",
    },
    {
      id: "2",
      question: "How can I configure notifications?",
      answer:
        "Navigate to Settings > Notifications. You can configure email, push, and in-app notifications for different events, set quiet hours, and test notification delivery.",
      category: "settings",
    },
    {
      id: "3",
      question: "How do I create a custom report?",
      answer:
        "Go to Reports > Generate Report. Choose report type, date range, and options. Reports can include charts, recommendations, and can be scheduled for automatic generation.",
      category: "reports",
    },
    {
      id: "4",
      question: "What data is shown in the dashboard?",
      answer:
        "The dashboard shows key metrics including revenue, page views, clicks, CTR, CPC, and CPM. Data can be filtered by time period (7d, 30d, 90d) and includes trends and recommendations.",
      category: "dashboard",
    },
    {
      id: "5",
      question: "How do I manage ad units?",
      answer:
        "Navigate to Ad Units to view, create, and manage your ad units. You can track performance, pause/activate units, and get optimization suggestions based on performance data.",
      category: "ad-units",
    },
    {
      id: "6",
      question: "Can I export my data?",
      answer:
        "Yes, you can export data from Historical Data page in JSON or CSV format. Reports can also be downloaded as PDF files from the Reports section.",
      category: "data",
    },
    {
      id: "7",
      question: "How do I configure system settings?",
      answer:
        "Admin users can access Settings from the sidebar. Here you can configure timezone, currency, data retention, notification preferences, and other system parameters.",
      category: "settings",
    },
    {
      id: "8",
      question: "What user roles are available?",
      answer:
        "There are two roles: Admin (full access including user management and data generation) and User (standard access to view metrics and reports).",
      category: "admin",
    },
    {
      id: "9",
      question: "How do I block ads or sites?",
      answer:
        "Navigate to Allow & Block Ads to manage blocked sites, categories, and specific ads. You can add sites manually or import lists.",
      category: "blocking",
    },
    {
      id: "10",
      question: "How does optimization work?",
      answer:
        "The Optimization section provides AI-powered suggestions to improve revenue, A/B testing tools, and automated optimization rules.",
      category: "optimization",
    },
  ];

  const guides: Guide[] = [
    {
      id: "1",
      title: "Getting Started Guide",
      description: "Learn the basics of using AdSense Clone",
      icon: BookOpenIcon,
      url: "/guides/getting-started",
      category: "general",
    },
    {
      id: "2",
      title: "Data Generation Tutorial",
      description: "Step-by-step guide to generating sample data",
      icon: ChartBarIcon,
      url: "/guides/data-generation",
      category: "admin",
    },
    {
      id: "3",
      title: "Ad Unit Optimization",
      description: "Best practices for ad placement and optimization",
      icon: BoltIcon,
      url: "/guides/ad-optimization",
      category: "ad-units",
    },
    {
      id: "4",
      title: "Performance Reports",
      description: "Creating and understanding performance metrics",
      icon: DocumentTextIcon,
      url: "/guides/performance-reports",
      category: "reports",
    },
    {
      id: "5",
      title: "Blocking & Filtering",
      description: "Managing blocked content and site filters",
      icon: ShieldCheckIcon,
      url: "/guides/blocking",
      category: "blocking",
    },
    {
      id: "6",
      title: "Optimization Strategies",
      description: "Advanced optimization techniques",
      icon: LightBulbIcon,
      url: "/guides/optimization",
      category: "optimization",
    },
  ];

  const categories = [
    {
      id: "all",
      name: "All Topics",
      count: faqs.length,
      icon: QuestionMarkCircleIcon,
    },
    {
      id: "dashboard",
      name: "Dashboard",
      count: faqs.filter((f) => f.category === "dashboard").length,
      icon: ChartBarIcon,
    },
    {
      id: "ad-units",
      name: "Ad Units",
      count: faqs.filter((f) => f.category === "ad-units").length,
      icon: BoltIcon,
    },
    {
      id: "reports",
      name: "Reports",
      count: faqs.filter((f) => f.category === "reports").length,
      icon: DocumentTextIcon,
    },
    {
      id: "settings",
      name: "Settings",
      count: faqs.filter((f) => f.category === "settings").length,
      icon: CogIcon,
    },
    {
      id: "admin",
      name: "Admin",
      count: faqs.filter((f) => f.category === "admin").length,
      icon: UserGroupIcon,
    },
    {
      id: "optimization",
      name: "Optimization",
      count: faqs.filter((f) => f.category === "optimization").length,
      icon: LightBulbIcon,
    },
    {
      id: "blocking",
      name: "Blocking",
      count: faqs.filter((f) => f.category === "blocking").length,
      icon: ShieldCheckIcon,
    },
  ];

  const filteredFAQs = faqs.filter(
    (faq) =>
      (activeCategory === "all" || faq.category === activeCategory) &&
      (faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const toggleFAQ = (id: string) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Contact form submitted:", contactForm);
    setShowContactForm(false);
    setContactForm({
      name: "",
      email: "",
      subject: "general",
      message: "",
    });
    alert("Thank you for your message. We'll get back to you within 24 hours.");
  };

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl lg:text-2xl font-normal text-[#202124]">
            Help & Support
          </h1>
          <p className="text-sm text-[#5f6368] mt-1">
            Find answers, guides, and support resources
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowContactForm(true)}
            className="px-4 py-2 bg-[#1a73e8] text-white rounded-lg hover:bg-[#0d62d9] transition-colors text-sm"
          >
            Contact Support
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg border border-[#dadce0] p-4 lg:p-6">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#5f6368]" />
          <input
            type="text"
            placeholder="Search for help articles, guides, or FAQs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-[#dadce0] rounded-lg focus:outline-none focus:border-[#1a73e8] focus:ring-1 focus:ring-[#1a73e8]"
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-[#5f6368]">Popular:</span>
          {["dashboard", "ad units", "reports", "settings"].map((term) => (
            <button
              key={term}
              onClick={() => setSearchTerm(term)}
              className="text-sm text-[#1a73e8] hover:text-[#0d62d9] bg-[#e8f0fe] hover:bg-[#d2e3fc] px-3 py-1 rounded-full transition-colors"
            >
              {term}
            </button>
          ))}
        </div>
      </div>

      {/* Categories */}
      <div className="overflow-x-auto pb-2">
        <div className="flex space-x-2 min-w-max">
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-colors whitespace-nowrap ${
                  activeCategory === category.id
                    ? "bg-[#1a73e8] text-white"
                    : "bg-white border border-[#dadce0] text-[#5f6368] hover:border-[#1a73e8] hover:text-[#1a73e8]"
                }`}
              >
                <Icon className="h-5 w-5" />
                <span>{category.name}</span>
                <span className="text-xs opacity-75">({category.count})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - FAQs */}
        <div className="lg:col-span-2 space-y-6">
          {/* FAQs */}
          <div className="bg-white rounded-lg border border-[#dadce0] overflow-hidden">
            <div className="p-4 lg:p-6 border-b border-[#dadce0] bg-[#f8f9fa]">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <QuestionMarkCircleIcon className="h-6 w-6 text-[#1a73e8]" />
                  <div>
                    <h3 className="font-medium text-[#202124]">
                      Frequently Asked Questions
                    </h3>
                    <p className="text-sm text-[#5f6368]">
                      {filteredFAQs.length} question
                      {filteredFAQs.length !== 1 ? "s" : ""} in{" "}
                      {activeCategory === "all"
                        ? "all categories"
                        : categories.find((c) => c.id === activeCategory)?.name}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="divide-y divide-[#f1f3f4]">
              {filteredFAQs.length === 0 ? (
                <div className="p-8 text-center">
                  <QuestionMarkCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Questions Found
                  </h3>
                  <p className="text-gray-600">
                    Try searching for something else or browse by category
                  </p>
                </div>
              ) : (
                filteredFAQs.map((faq) => (
                  <div
                    key={faq.id}
                    className="p-4 lg:p-6 hover:bg-[#f8f9fa] transition-colors"
                  >
                    <button
                      onClick={() => toggleFAQ(faq.id)}
                      className="w-full flex items-center justify-between text-left"
                    >
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="flex-shrink-0 mt-1">
                          {expandedFAQ === faq.id ? (
                            <ChevronDownIcon className="h-5 w-5 text-[#5f6368]" />
                          ) : (
                            <ChevronRightIcon className="h-5 w-5 text-[#5f6368]" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-[#202124] mb-2">
                            {faq.question}
                          </h4>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs px-2 py-1 bg-[#f1f3f4] text-[#5f6368] rounded">
                              {
                                categories.find((c) => c.id === faq.category)
                                  ?.name
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>

                    {expandedFAQ === faq.id && (
                      <div className="mt-4 ml-9">
                        <div className="p-4 bg-[#f8f9fa] rounded-lg border border-[#dadce0]">
                          <p className="text-sm text-[#5f6368] leading-relaxed">
                            {faq.answer}
                          </p>
                          <div className="mt-4 flex items-center space-x-4">
                            <button className="text-sm text-[#1a73e8] hover:text-[#0d62d9] font-medium">
                              Was this helpful?
                            </button>
                            <span className="text-[#dadce0]">|</span>
                            <button className="text-sm text-[#1a73e8] hover:text-[#0d62d9] font-medium">
                              Share this answer
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div className="bg-gradient-to-r from-[#e8f0fe] to-[#f1f8e9] border border-[#aecbfa] rounded-lg p-6">
            <div className="flex items-start space-x-4">
              <div className="h-10 w-10 bg-[#1a73e8] rounded-full flex items-center justify-center flex-shrink-0">
                <LightBulbIcon className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-[#202124] mb-3">
                  Quick Actions
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Link
                    to="/feedback"
                    className="p-4 bg-white/80 backdrop-blur-sm rounded border border-white/20 hover:border-[#1a73e8] transition-colors"
                  >
                    <h4 className="font-medium text-[#202124] mb-1">
                      Submit Feedback
                    </h4>
                    <p className="text-sm text-[#5f6368]">
                      Share your ideas and suggestions
                    </p>
                  </Link>
                  <Link
                    to="/optimization"
                    className="p-4 bg-white/80 backdrop-blur-sm rounded border border-white/20 hover:border-[#1a73e8] transition-colors"
                  >
                    <h4 className="font-medium text-[#202124] mb-1">
                      View Optimization Tips
                    </h4>
                    <p className="text-sm text-[#5f6368]">
                      Improve your ad performance
                    </p>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Guides & Support */}
        <div className="space-y-6">
          {/* Help Guides */}
          <div className="bg-white rounded-lg border border-[#dadce0] p-6">
            <div className="flex items-center space-x-3 mb-6">
              <BookOpenIcon className="h-6 w-6 text-[#1a73e8]" />
              <div>
                <h3 className="font-medium text-[#202124]">Help Guides</h3>
                <p className="text-sm text-[#5f6368]">Step-by-step tutorials</p>
              </div>
            </div>

            <div className="space-y-4">
              {guides
                .filter(
                  (guide) =>
                    activeCategory === "all" ||
                    guide.category === activeCategory
                )
                .slice(0, 4)
                .map((guide) => {
                  const Icon = guide.icon;
                  return (
                    <Link
                      key={guide.id}
                      to={guide.url}
                      className="flex items-center space-x-3 p-3 border border-[#dadce0] rounded-lg hover:border-[#1a73e8] hover:bg-[#f8f9fa] transition-colors group"
                    >
                      <div className="h-10 w-10 bg-[#e8f0fe] rounded-lg flex items-center justify-center group-hover:bg-[#1a73e8] transition-colors">
                        <Icon className="h-5 w-5 text-[#1a73e8] group-hover:text-white transition-colors" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-[#202124] group-hover:text-[#1a73e8] transition-colors truncate">
                          {guide.title}
                        </h4>
                        <p className="text-sm text-[#5f6368] truncate">
                          {guide.description}
                        </p>
                      </div>
                    </Link>
                  );
                })}
            </div>

            <button className="w-full mt-4 px-4 py-2 border border-[#dadce0] text-[#1a73e8] rounded-lg hover:bg-[#f8f9fa] transition-colors text-sm font-medium">
              View all guides →
            </button>
          </div>

          {/* Support Options */}
          <div className="space-y-4">
            <div className="bg-white rounded-lg border border-[#dadce0] p-6 text-center">
              <div className="h-12 w-12 bg-[#e8f0fe] rounded-full flex items-center justify-center mx-auto mb-4">
                <ChatBubbleLeftRightIcon className="h-6 w-6 text-[#1a73e8]" />
              </div>
              <h4 className="font-medium text-[#202124] mb-2">Live Chat</h4>
              <p className="text-sm text-[#5f6368] mb-4">
                Chat with our support team
              </p>
              <button className="px-4 py-2 bg-[#1a73e8] text-white rounded-lg hover:bg-[#0d62d9] transition-colors text-sm">
                Start Chat
              </button>
              <p className="text-xs text-[#5f6368] mt-2">Available 24/7</p>
            </div>

            <div className="bg-white rounded-lg border border-[#dadce0] p-6 text-center">
              <div className="h-12 w-12 bg-[#e6f4ea] rounded-full flex items-center justify-center mx-auto mb-4">
                <PhoneIcon className="h-6 w-6 text-[#0d652d]" />
              </div>
              <h4 className="font-medium text-[#202124] mb-2">Phone Support</h4>
              <div className="text-lg font-medium text-[#202124] mb-2">
                1-800-ADSENSE
              </div>
              <p className="text-xs text-[#5f6368]">Mon-Fri, 9AM-5PM EST</p>
            </div>
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-lg border border-[#dadce0] p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-medium text-[#202124]">System Status</h3>
            <p className="text-sm text-[#5f6368]">
              Current status of all services
            </p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-[#5f6368]">
            <ArrowPathIcon className="h-4 w-4" />
            <span>Last updated: Just now</span>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { service: "API", status: "operational", color: "green" },
            { service: "Database", status: "operational", color: "green" },
            { service: "Reports", status: "operational", color: "green" },
            { service: "Notifications", status: "operational", color: "green" },
          ].map((item) => (
            <div
              key={item.service}
              className="p-4 border border-[#dadce0] rounded-lg hover:border-[#1a73e8] transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#202124]">
                  {item.service}
                </span>
                <div className="flex items-center space-x-1">
                  <div
                    className={`h-2 w-2 rounded-full ${
                      item.color === "green"
                        ? "bg-[#0d652d]"
                        : item.color === "yellow"
                        ? "bg-[#e37400]"
                        : "bg-[#d33426]"
                    }`}
                  ></div>
                  <span className="text-xs text-[#5f6368]">{item.status}</span>
                </div>
              </div>
              <div className="h-1 bg-[#f1f3f4] rounded-full overflow-hidden">
                <div
                  className={`h-full ${
                    item.color === "green"
                      ? "bg-[#0d652d]"
                      : item.color === "yellow"
                      ? "bg-[#e37400]"
                      : "bg-[#d33426]"
                  }`}
                  style={{ width: "100%" }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Contact Support
                  </h3>
                  <p className="text-sm text-gray-600">
                    We'll respond within 24 hours
                  </p>
                </div>
                <button
                  onClick={() => setShowContactForm(false)}
                  className="p-2 text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={contactForm.name}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, email: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <select
                    value={contactForm.subject}
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        subject: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="general">General Question</option>
                    <option value="technical">Technical Issue</option>
                    <option value="feature">Feature Request</option>
                    <option value="billing">Billing Question</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message *
                  </label>
                  <textarea
                    required
                    value={contactForm.message}
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        message: e.target.value,
                      })
                    }
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder="Describe your issue or question..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowContactForm(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Send Message
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Help;
